import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {getAssetUrl} from '@ui/utils/urls/get-asset-url';
import {isAbsoluteUrl} from '@ui/utils/urls/is-absolute-url';

export type ImageSize = 'sm' | 'md' | 'lg' | 'original';

export type ImageKind = 'poster' | 'backdrop' | 'profile';

interface Options {
  size?: ImageSize;
}

interface SrcSetOptions {
  kind?: ImageKind;
}

/**
 * Yerel WebP variant'larının GERÇEK piksel genişlikleri. Backend
 * `ImageService::SRCSET_WIDTHS` ile bire bir senkron tutulmalıdır.
 *
 * D35: srcset descriptor'ı dosyanın gerçek piksel genişliği olmak
 * zorunda. Önceden 768w/1024w/1280w (yalan) kullanılıyordu → tarayıcı
 * small'ı yeterli sanıp DPR=2 retinada 4-8x büyütüyordu = bulanık.
 */
const LOCAL_VARIANT_WIDTHS: Record<ImageKind, {sm: number; md: number; lg: number}> = {
  poster: {sm: 342, md: 500, lg: 780},
  backdrop: {sm: 300, md: 780, lg: 1280},
  profile: {sm: 342, md: 500, lg: 632},
};

/**
 * Variant cache-buster (D35). 30 günlük browser cache'inde eski 154/342 px
 * boyutlu dosyaları zorla bypass eder. ImageService variant boyutları
 * değiştiğinde bu sürüm artırılır.
 */
const VARIANT_CACHE_BUSTER = 'v=2';

function appendCacheBuster(url: string): string {
  if (!/\/(poster|backdrop|profile)-(small|medium|large|original)\.webp/.test(url)) {
    return url;
  }
  return url.includes('?')
    ? `${url}&${VARIANT_CACHE_BUSTER}`
    : `${url}?${VARIANT_CACHE_BUSTER}`;
}

/** TMDB and legacy/alternate TMDB image host patterns. */
function isTmdbImageUrl(src: string): boolean {
  const lower = src.toLowerCase();
  return (
    lower.includes('image.tmdb.org') ||
    lower.includes('themoviedb.org/t/p/') ||
    lower.includes('tmdb.org/t/p/')
  );
}

/** Protocol-relative URLs are not absolute per isAbsoluteUrl() and were wrongly prefixed with site base. */
function normalizeImageSrcString(src: string): string {
  if (src.startsWith('//')) {
    return `https:${src}`;
  }
  return src;
}

/** Local responsive variants (original → small/medium/large) only for this site / storage, not arbitrary remote URLs. */
function shouldApplyLocalSizing(src: string): boolean {
  const base = (getBootstrapData().settings.base_url || '').replace(/\/$/, '');
  const asset = (getBootstrapData().settings.asset_url || '').replace(/\/$/, '');
  // Konu 13: görseller artık cdn.cine5.com'dan gelebiliyor. Bu host tanınmazsa
  // variant swap uygulanmaz ve tarayıcı hep 'original' (780/1280px) indirir →
  // D35'te düzeltilen bulanıklık/ağırlık bugu geri gelir.
  const cdn = (getBootstrapData().settings.cdn_url || '').replace(/\/$/, '');
  if (base && src.startsWith(base)) {
    return true;
  }
  if (asset && src.startsWith(asset)) {
    return true;
  }
  if (cdn && src.startsWith(cdn)) {
    return true;
  }
  const lower = src.toLowerCase();
  return /^https?:\/\//i.test(src) && lower.includes('/storage/');
}

export function useImageSrc(
  src: string | undefined,
  {size}: Options = {},
): string | undefined {
  if (!src) return;
  if (!size) size = 'original';

  src = normalizeImageSrcString(src.trim());

  if (isTmdbImageUrl(src)) {
    return getTmdbSrc(src, size);
  }

  if (isAbsoluteUrl(src)) {
    if (shouldApplyLocalSizing(src)) {
      return getLocalSrc(src, size);
    }
    return src;
  }

  return getLocalSrc(getAssetUrl(src), size);
}

export function useImageSrcSet(
  src: string | undefined,
  {kind = 'poster'}: SrcSetOptions = {},
): string | undefined {
  if (!src) return;

  src = normalizeImageSrcString(src.trim());

  if (isTmdbImageUrl(src) || src.toLowerCase().includes('themoviedb')) {
    // Mobile devices with DPR>=2 need higher-res images even at narrow viewports.
    // Using w780 @ 768w and w1280 @ 1280w avoids blurry mobile backdrops/posters.
    // Capped at w1280 (~150KB) on purpose: "original" backdrops are 1.5-4MB (4K)
    // and tank the homepage hero LCP. Full-bleed backdrops sit under heavy gradient
    // overlays, so w1280 is visually indistinguishable while ~9x lighter.
    const normalized = src.replace(/\/w\d+\//, '/original/');
    return `${normalized.replace(/original|w1280/, 'w500')} 500w, ${normalized.replace(
      /original|w1280/,
      'w780',
    )} 780w, ${normalized.replace(/original|w1280/, 'w1280')} 1280w`;
  }

  if (isAbsoluteUrl(src) && !shouldApplyLocalSizing(src)) {
    return undefined;
  }

  const w = LOCAL_VARIANT_WIDTHS[kind];
  return `${appendCacheBuster(src.replace(/original/, 'small'))} ${w.sm}w, ${appendCacheBuster(
    src.replace(/original/, 'medium'),
  )} ${w.md}w, ${appendCacheBuster(src.replace(/original/, 'large'))} ${w.lg}w`;
}

function getTmdbSrc(initialSrc: string, size: ImageSize): string {
  switch (size) {
    case 'sm':
      return initialSrc.replace(/original|w1280/, 'w92');
    case 'md':
      return initialSrc.replace(/original|w1280/, 'w300');
    case 'lg':
      return initialSrc.replace(/original|w1280/, 'w500');
    default:
      return initialSrc;
  }
}

function getLocalSrc(initialSrc: string, size: ImageSize): string {
  let resolved: string;
  switch (size) {
    case 'sm':
      resolved = initialSrc.replace('original', 'small');
      break;
    case 'md':
      resolved = initialSrc.replace('original', 'medium');
      break;
    case 'lg':
      resolved = initialSrc.replace('original', 'large');
      break;
    default:
      resolved = initialSrc;
  }
  return appendCacheBuster(resolved);
}
